<script lang="ts" setup>
import {
  defineAsyncComponent,
  onMounted,
  reactive,
  ref,
  useRoute,
  watch,
} from '#imports';
import { useService } from '@kaokei/use-vue-service';
import armsRum from '@arms/rum-browser';
import { getClassTutor } from '@core/api/im';
import teacherAvatar from '@core/assets/image/im/teacher@2x.jpg';
import { UserService } from '@core/services/user.service';
import type { ImUserVO } from '@core/types/im';

const LazyImChat = defineAsyncComponent(() => import('./ImChat.vue'));

const inited = ref(false);
const show = ref(false);
const users = reactive({
  self: {} as ImUserVO,
  peer: {} as ImUserVO,
});
const route = useRoute();
const userService = useService(UserService);

// 目前只有3个页面可能会显示IM入口
const whitelist = [
  'oj_training_detail',
  'oj_training_detail_problem',
  'oj_problem_detail',
];

function updateChatVisible() {
  if (!userService.isLogin()) {
    // 必须要求登录
    return;
  }
  const routeName = route.name as string;
  if (route.query.tid && whitelist.includes(routeName)) {
    // 路由中必须有gid和tid，并且在指定的路由白名单中
    tryInit();
  } else {
    // 不满足基本条件则隐藏IM
    show.value = false;
  }
}

function tryInit() {
  const user = userService.user || {};
  users.self = {
    id: user.userId,
    name: user.nickname,
    avatar: user.avatar,
  };

  const model = {
    gid: route.query.gid as string,
    tid: route.query.tid as string,
  };
  getClassTutor(model)
    .then((res) => {
      if (res?.teacherId) {
        // 必须返回老师ID才能初始化
        if (res.teacherId === users.peer.id) {
          // 当前IM已经初始化，并且老师ID一致
          show.value = true;
        } else {
          // 返回了新的老师ID
          // 如果是第一次初始化，那么inited会控制组件初始化
          // 如果是重新初始化，那么key属性会控制组件重新初始化
          users.peer = {
            id: res.teacherId,
            name: res.nickname,
            avatar: teacherAvatar,
          };
          show.value = true;
          inited.value = true;
        }
      } else {
        // [埋点 G] getClassTutor 未返回 teacherId，IM 不显示
        // 用于感知"学生有 tid 参数但没有绑定老师"的配置问题
        console.warn('[IM] getClassTutor 未返回 teacherId，IM 不显示');
        armsRum.sendCustom({
          type: 'custom',
          name: 'oj_im_init_no_teacher',
          value: 1,
        });
        show.value = false;
      }
    })
    .catch((err) => {
      // [埋点 H] getClassTutor 接口调用异常，IM 静默不显示
      console.error('[IM] getClassTutor 接口异常', err);
      armsRum.sendCustom({
        type: 'custom',
        name: 'oj_im_init_api_error',
        value: 1,
      });
      show.value = false;
    });
}

watch(
  () => route.name as string,
  () => updateChatVisible(),
);

onMounted(() => updateChatVisible());
</script>

<template>
  <div v-if="inited">
    <LazyImChat
      v-show="show"
      :key="users.peer.id"
      type="ms"
      :self="users.self"
      :peer="users.peer"
    />
  </div>
</template>
